import React, { useState } from 'react';

import { isAxiosError } from 'services/axios';
import { toast } from 'shared/toast';

import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import { requestAutomaticLink } from './service';

type StrategyKey = 'services' | 'service_price_informed' | 'complete_mixed';

type ModalAutomaticLinkStrategySelectProps = ModalRootProps & {
  data: {
    budgetId: string;
  };
};

type Strategy = {
  key: StrategyKey;
  title: string;
  description: React.ReactNode;
};

const STRATEGIES: Strategy[] = [
  {
    key: 'services',
    title: 'Vínculo por descrição de serviços',
    description: (
      <VStack align="start" spacing={2}>
        <Text>
          O sistema consultará as bases configuradas em{' '}
          <Text as="span" fontWeight="semibold" color="gray.900">
            Configurações
          </Text>
          , buscará correspondências para a descrição de cada item do orçamento
          e substituirá cada item pelo serviço com maior correspondência.
        </Text>

        <Text>
          Caso não seja encontrada uma correspondência para um item, ele
          permanecerá sem vínculo e poderá ser vinculado manualmente
          posteriormente.
        </Text>
      </VStack>
    ),
  },
  // {
  //   key: 'service_price_informed',
  //   title: 'Serviço a Preço Informado',
  //   description: 'Cria um SPI e distribui os valores pelos grupos de insumos.',
  // },
  // {
  //   key: 'complete_mixed',
  //   title: 'Completo / Misto',
  //   description: `Primeiro tenta vincular por Serviços. Caso não encontre correspondência, gera um Serviço a Preço Informado.`,
  // },
];

const ModalAutomaticLinkStrategySelect: React.FC<
  ModalAutomaticLinkStrategySelectProps
> = ({ data, onConfirm, handleClose, ...restProps }) => {
  const [selectedStrategy, setSelectedStrategy] =
    useState<StrategyKey>('services');
  const [isLoading, setIsLoading] = useState(false);

  const handleStartAutomaticLink = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);

      await requestAutomaticLink(data.budgetId);

      toast({
        status: 'success',
        description:
          'Vínculo automático solicitado com sucesso. Você será notificado quando estiver pronto.',
      });

      if (onConfirm) {
        onConfirm(selectedStrategy);
      }

      handleClose();
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? (err.response?.data as { message?: string })?.message
        : null;

      toast({
        status: 'error',
        description:
          errorMessage ||
          'Não foi possível iniciar o vínculo automático. Tente novamente em instantes.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal {...restProps} size="4xl" scrollBehavior="inside">
      <ModalOverlay />

      <ModalContent>
        <ModalHeader pb={1}>Vínculo automático</ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />

        <ModalBody>
          <Text color="gray.600" mb={4}>
            Como os serviços do QExpress serão vinculados ao orçamento?
          </Text>

          <RadioGroup
            value={selectedStrategy}
            onChange={(value) => setSelectedStrategy(value as StrategyKey)}
          >
            <VStack spacing={3} align="stretch">
              {STRATEGIES.map((strategy) => {
                // const isSelected = strategy.key === selectedStrategy;

                return (
                  <Box
                    key={strategy.key}
                    // borderWidth="1px"
                    // borderRadius="md"
                    // borderColor={isSelected ? 'blue.100' : 'gray.200'}
                    // bg={isSelected ? 'blue.50' : 'white'}
                    // p={4}
                    cursor="pointer"
                    onClick={() => setSelectedStrategy(strategy.key)}
                  >
                    <Flex align="flex-start" gap={3}>
                      <Radio
                        value={strategy.key}
                        colorScheme="blue"
                        mt={1}
                        aria-label={strategy.title}
                        display="none"
                      />

                      <Box w="100%">
                        <Flex
                          align={{ base: 'flex-start', sm: 'center' }}
                          direction={{ base: 'column', sm: 'row' }}
                          gap={2}
                          mb={2}
                        >
                          <Text fontWeight="bold" color="gray.800">
                            {strategy.title}
                          </Text>
                        </Flex>

                        <Box mb={3}>{strategy.description}</Box>
                      </Box>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          </RadioGroup>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={handleClose} isDisabled={isLoading}>
            Cancelar
          </Button>
          <Button
            colorScheme="green"
            onClick={handleStartAutomaticLink}
            isLoading={isLoading}
            isDisabled={isLoading}
            ml={3}
          >
            Iniciar vínculo
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalAutomaticLinkStrategySelect;
